import React, { useMemo } from 'react';
import classnames from 'classnames';
import Trans from 'components/Common/Translation/Trans';
import { useTranslation } from 'react-i18next';
import { formatDiscountPercentage, formatPrice } from 'services/formatter';
import { addCountryClassname } from 'services/generic';
import useCurrentCountry from 'hooks/useCurrentCountry';

import recycling from 'assets/img/recycling.png';

import styles from './ProductPrice.module.scss';

/**
 * Product price.
 */
function ProductPrice({
  discountPercentage,
  lot,
  preDiscountPrice,
  price,
  selectedSize,
  ecopart,
  priceVolume,
  isUniquePrice,
  redPrice,
  liteMode,
  classes,
}: ProductPriceType): JSX.Element {
  const { t } = useTranslation();
  const { isFr } = useCurrentCountry();
  const hasDiscount = preDiscountPrice && preDiscountPrice !== '0';
  const discountPercentageFormatting = useMemo(
    () =>
      hasDiscount && discountPercentage
        ? formatDiscountPercentage(+discountPercentage)
        : '',
    [hasDiscount, discountPercentage]
  );

  return (
    <div className={classnames(styles.container, classes?.container)}>
      <div
        className={classnames(
          styles.productPriceContainer,
          liteMode ? styles.productPriceLightContainer : '',
          classes?.productPriceContainer
        )}
      >
        {hasDiscount && (
          <div
            className={classnames(
              styles.discountContainer,
              classes?.discountContainer
            )}
          >
            <span
              className={classnames(
                styles.discountPercentage,
                classes?.discountPercentage
              )}
            >
              <Trans
                i18nKey="LABEL_DISCOUNT_FORMAT"
                values={{ amount: discountPercentageFormatting }}
              />
            </span>
            <span
              className={classnames(
                styles.discountOldPrice,
                classes?.discountOldPrice
              )}
            >
              {formatPrice(preDiscountPrice)}
            </span>
          </div>
        )}
        <div
          className={classnames(
            styles.priceContainer,
            classes?.finalPriceContainer
          )}
        >
          {!isUniquePrice && (
            <span
              className={classnames(
                styles.labelFrom,
                selectedSize ? styles.hideLabelFrom : '',
                classes?.labelFrom
              )}
            >
              {t('LABEL_FROM')}
            </span>
          )}
          <span
            className={classnames(styles.price, classes?.price, {
              [styles.redPrice]: redPrice === '1',
              [styles.greenPrice]: redPrice === '2',
            })}
          >
            {formatPrice(price)}
            {lot && (
              <>
                &nbsp;
                <span className={styles.lot}>
                  {t('PRODUCT_LOT', { number: lot })}
                </span>
              </>
            )}
          </span>
        </div>
      </div>

      {!liteMode && (
        <>
          {ecopart && (
            <div
              className={`${styles.ecopartContainer} ${addCountryClassname(
                styles
              )}`}
            >
              <img
                src={recycling}
                className={styles.recyclingSymbol}
                alt="recycling"
              />

              {isFr ? (
                <span className={styles.ecopartValue}>{ecopart}</span>
              ) : (
                <>
                  <span>{t('LABEL_INCLUDING')}</span>
                  <span className={styles.ecopartValue}>{ecopart}</span>
                  <span>{t('LABEL_FOR_ECOPARTICIPATION')}</span>
                </>
              )}
            </div>
          )}

          {priceVolume && (
            <div className={styles.prixVolContainer}>
              <span className={styles.prixVolValue}>{priceVolume}</span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export type ProductPriceType = {
  /** Discount percentage value */
  discountPercentage?: string;
  /** Value of metaLot */
  lot: string;
  /** Price value to display when there is a discount */
  preDiscountPrice?: string;
  /** Product price */
  price: string;
  /** Selected product size */
  selectedSize: string | undefined;
  /** Value of eco-participation */
  ecopart?: string;
  /** Value of price volume */
  priceVolume?: string;
  /** Value of unique price */
  isUniquePrice?: boolean;
  /** Indicates if the component is used on mobile */
  liteMode?: boolean;
  /** Value of redPrice */
  redPrice?: string;
  classes?: Partial<ProductPriceClasses>;
};

export interface ProductPriceClasses {
  /** Wrapper class */
  container?: string;

  /** Container price */
  productPriceContainer?: string;

  /** Discount container */
  discountContainer?: string;

  /** Discount percentage value */
  discountPercentage?: string;

  /** Discount old price value */
  discountOldPrice?: string;

  /** Final price container */
  finalPriceContainer?: string;

  /** Price from label */
  labelFrom?: string;

  /** Price value */
  price?: string;
}

export default ProductPrice;
